import { lazy } from "react";
import { isEditableElement } from "@/utils/dom";
import { renameEnvironmentAtom, renamingEnvironmentIdAtom } from "@/atoms/ui-state";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { IconStop } from "@/assets/icons/geist/IconStop";
import { IconOnaChevronRight } from "@/assets/icons/IconOnaChevronRight";
import { withGitpodSize } from "@/assets/icons/utils";
import { IconPlusLarge as IconPlusLargeRaw } from "central-icons/IconPlusLarge";
import { EnvironmentLifetimeExceededModal } from "@/components/environments/EnvironmentLifetimeExceededModal";

const EnvironmentLockIndicator = lazy(() =>
    import("@/components/environments/EnvironmentLockIndicator").then((m) => ({
        default: m.EnvironmentLockIndicator,
    })),
);
import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import { StatusDot } from "@/components/flexkit/status-dot/StatusDot";
import { getConversationDotProps } from "@/routes/environments/conversation-status";
import { ConversationMetadata } from "@/routes/environments/ConversationMetadata";
import { useFlagSuperOnaEnabled } from "@/hooks/use-flag";
import { Notification } from "@/components/flexkit/badge/Notification";
import { Button } from "@/components/flexkit/Button";
import { Collapsible, CollapsibleContent } from "@/components/flexkit/Collapsible";
import { Link } from "@/components/flexkit/Link";
import { TextShimmer } from "@/components/flexkit/TextShimmer";
import { FlexTruncatedText } from "@/components/FlexTruncatedText";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { SkeletonBlock, SkeletonText } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { VisibleOnActive } from "@/components/ui/VisibleOnActive";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useEnvironmentName } from "@/hooks/use-environment-name";
import { useEnvironmentsByProjects, type EnvironmentsGroup } from "@/hooks/use-environments-by-projects";

import { useExitTransition } from "@/hooks/use-exit-transition";
import { useBranch } from "@/hooks/use-git-reference";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useIsPointerCoarse } from "@/hooks/use-is-pointer-coarse";
import { usePerformance } from "@/hooks/use-performance";
import { TrackLocations } from "@/hooks/use-segment";
import {
    useGroupedAgentExecutions,
    type PlainAgentExecution,
    type ChildEnvironmentInfo,
} from "@/queries/agent-queries";
import { type PlainEditor } from "@/queries/editor-queries";
import { useCanStartEnvironment } from "@/hooks/use-can-start-environment";
import {
    useStartEnvironment,
    useStopEnvironment,
    useUpdateEnvironment,
    type PlainEnvironment,
} from "@/queries/environment-queries";
import { usePrefetchProjectsByIds, useProject } from "@/queries/project-queries";
import { CreateEnvironmentButton } from "@/routes/environments/create/CreateEnvironmentButton";
import { getDetailsURL } from "@/routes/environments/details-url";
import { EnvironmentActionsMenu } from "@/routes/environments/details/EnvironmentActionsMenu";
import { GitStatusTooltipContentFromGit } from "@/routes/environments/details/GitStatusPopOver";
import {
    environmentHasReachedStablePhase,
    showStart,
    showStop,
    stateFromEnvironment,
} from "@/routes/environments/phase";
import {
    agentExecutionLoading,
    agentExecutionNeedsAttention,
    mostRecentAgentExecution,
} from "@/utils/agent-executions";
import { getProjectDisplayName } from "@/utils/environment";
import { useLifetimeEnforcement } from "@/hooks/use-lifetime-enforcement";

import { formatError } from "@/utils/errors";
import { haptic } from "@/utils/haptic";
import { formatChangesCount, gitStatusChangesCount } from "@/utils/git-status";
import type { PlainMessage } from "@bufbuild/protobuf";
import { type Node } from "@react-types/shared";
import { IconCheckmark2Small } from "central-icons/IconCheckmark2Small";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import {
    EnvironmentPhase,
    ListEnvironmentsRequest_ArchivalStatus,
    UpdateEnvironmentRequest,
    UpdateEnvironmentRequest_Metadata,
    type EnvironmentGitStatus,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import { atom, useAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import React, { Suspense, useCallback, useEffect, useId, useMemo, useRef, useState, type FC } from "react";
import { useGridList, useGridListItem, useLongPress } from "react-aria";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Item, useListState, type ListState } from "react-stately";

const IconPlusLarge = withGitpodSize(IconPlusLargeRaw);

type AgentExecutionByEnvironment = Map<string, PlainAgentExecution[]>;

export const SidebarEnvironmentList: FC = () => {
    const { markDataLoadStart, markDataLoadComplete } = usePerformance({
        component: "SidebarEnvironmentList",
    });

    // Mark data loading start
    const dataLoadStart = markDataLoadStart();

    const { data: envsByProject, isLoading } = useEnvironmentsByProjects({
        // For now we still want to show environments created by agents in the sidebar
        includeAgentCreatedEnvironments: true,
        // Only show active environments since archiving is always enabled
        archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ACTIVE,
    });

    const { data: groupedAgentExecutions, isPending: isLoadingAgentExecutions } = useGroupedAgentExecutions();

    // Batch-fetch all projects referenced by environment groups to seed individual caches.
    // This avoids N individual getProject calls from each Group component.
    const projectIds = useMemo(
        () => envsByProject.map((g) => g.projectId).filter((id): id is string => !!id),
        [envsByProject],
    );
    usePrefetchProjectsByIds(projectIds);

    // Mark data loading complete when environments are loaded
    useEffect(() => {
        if (!isLoading && envsByProject) {
            markDataLoadComplete(dataLoadStart);
        }
    }, [isLoading, envsByProject, markDataLoadComplete, dataLoadStart]);

    // Build a map of all environments by ID for child environment lookups
    const environmentsById = useMemo(() => {
        const map = new Map<string, PlainEnvironment>();
        for (const group of envsByProject) {
            for (const env of group.environments) {
                map.set(env.id, env);
            }
        }
        return map;
    }, [envsByProject]);

    // Filter child environments out of groups so they only appear nested under their parent
    const childEnvIds = useMemo(
        () => groupedAgentExecutions?.childEnvironmentIds ?? new Set<string>(),
        [groupedAgentExecutions?.childEnvironmentIds],
    );
    const filteredEnvsByProject = useMemo(() => {
        if (childEnvIds.size === 0) {
            return envsByProject;
        }
        return envsByProject
            .map((group) => ({
                ...group,
                environments: group.environments.filter((env) => !childEnvIds.has(env.id)),
            }))
            .filter((group) => group.environments.length > 0);
    }, [envsByProject, childEnvIds]);

    if (isLoading || isLoadingAgentExecutions) {
        return <SidebarEnvironmentListSkeleton />;
    }

    return (
        <div
            className="contents"
            data-testid="environments-list"
            data-track-location={TrackLocations.SidebarEnvironmentList}
        >
            {filteredEnvsByProject.map((group) => (
                <Group
                    group={group}
                    key={`group-${group.projectId || group.repoFullName}`}
                    agentExecutionsByEnvironment={groupedAgentExecutions?.environmentScoped ?? new Map()}
                    childEnvironments={groupedAgentExecutions?.childEnvironments ?? new Map()}
                    environmentsById={environmentsById}
                />
            ))}
        </div>
    );
};

type ChildEnvironmentsByParent = Map<string, ChildEnvironmentInfo[]>;

const Group: FC<{
    group: EnvironmentsGroup;
    agentExecutionsByEnvironment: AgentExecutionByEnvironment;
    childEnvironments: ChildEnvironmentsByParent;
    environmentsById: Map<string, PlainEnvironment>;
}> = ({ group, agentExecutionsByEnvironment, childEnvironments, environmentsById }) => {
    const { isMobileViewport } = useIsMobileViewport();
    const { environmentId } = useParams();
    const { data: project, isLoading: isLoadingProject } = useProject(group.projectId);

    const shouldOpen = useMemo(() => {
        if (environmentId) {
            if (group.environments.some((env) => env.id === environmentId)) {
                return true;
            }
        }
        return group.environments.some(
            (env) =>
                env.status?.phase && [EnvironmentPhase.RUNNING, EnvironmentPhase.STARTING].includes(env.status.phase),
        );
    }, [environmentId, group.environments]);
    const [openState, setOpenState] = useState<"open" | "closed">(shouldOpen ? "open" : "closed");

    /**
     * When the group's `shouldOpen` computed state changes to true, we want to open the group.
     */
    useEffect(() => {
        if (shouldOpen) {
            setOpenState("open");
        }
    }, [shouldOpen]);

    const toggleOpen = useCallback(() => {
        setOpenState((current) => (current === "open" ? "closed" : "open"));
    }, []);

    const isProjectMissing = !!(!project && group.projectId && !isLoadingProject);

    return (
        <Collapsible open={openState === "open"} className="flex flex-col">
            <div
                className={cn(
                    "group flex h-9 w-full flex-row content-center items-center justify-between rounded-lg hover:bg-surface-hover",
                    "has-[>button:focus-visible]:outline has-[>button:focus-visible]:outline-1 has-[>button:focus-visible]:outline-offset-0 has-[>button:focus-visible]:outline-content-primary",
                )}
            >
                <button
                    onClick={toggleOpen}
                    aria-expanded={openState === "open"}
                    className="mr-2 flex min-w-0 grow flex-row content-center items-center focus:ring-0"
                    aria-label="Toggle Environment Group"
                    title={group.repoFullName ?? project?.metadata?.name}
                    data-tracking-id-none
                >
                    <div className="flex shrink-0 pl-1">
                        <div
                            className={cn(
                                "flex size-6 items-center justify-center transition-transform duration-150 ease-in-out",
                                openState === "open" ? "rotate-90" : "rotate-0",
                            )}
                        >
                            <IconOnaChevronRight size="base" className="text-content-muted" />
                        </div>
                    </div>
                    <div className={cn("flex min-w-0 pl-1 text-sm font-medium text-content-muted")} translate="no">
                        {group.repoFullName ? (
                            <FlexTruncatedText text={group.repoFullName} className="text-sm font-medium" />
                        ) : isProjectMissing ? (
                            "Deleted project"
                        ) : (
                            <SkeletonText size="sm" className="w-32" ready={!!project}>
                                <span className={cn("inline-block max-w-full truncate text-content-muted")}>
                                    {getProjectDisplayName(project, isProjectMissing)}
                                </span>
                            </SkeletonText>
                        )}
                    </div>
                </button>
                {project && (
                    <div
                        className={cn(
                            "mr-2 flex items-center",
                            isMobileViewport
                                ? "opacity-100"
                                : "opacity-0 group-focus-within:opacity-100 group-hover:opacity-100",
                        )}
                    >
                        <CreateEnvironmentButton
                            project={project}
                            LeadingIcon={IconPlusLarge}
                            variant={"ghost"}
                            size={"xs"}
                            className={cn("border-0 text-content-muted")}
                            onCreateSuccess={() => setOpenState("open")}
                        />
                    </div>
                )}
            </div>

            <CollapsibleContent>
                <EnvironmentsGridList
                    group={group}
                    agentExecutionsByEnvironment={agentExecutionsByEnvironment}
                    childEnvironments={childEnvironments}
                    environmentsById={environmentsById}
                />
            </CollapsibleContent>
        </Collapsible>
    );
};

const EnvironmentsGridList: FC<{
    group: EnvironmentsGroup;
    agentExecutionsByEnvironment: AgentExecutionByEnvironment;
    childEnvironments: ChildEnvironmentsByParent;
    environmentsById: Map<string, PlainEnvironment>;
}> = ({ group, agentExecutionsByEnvironment, childEnvironments, environmentsById }) => {
    const { data: project } = useProject(group.projectId);

    const state = useListState<PlainEnvironment>({
        items: group.environments,
        children: (item) => <Item key={item.id}>{item.id}</Item>,
    });
    const ref = useRef<HTMLUListElement>(null);
    const { gridProps } = useGridList(
        {
            "aria-label":
                "Environments List for " + (project?.metadata?.name || group.repoFullName || "Unknown Project"),
        },
        state,
        ref,
    );

    const { onKeyDownCapture, ...ariaProps } = gridProps;

    return (
        <ul
            {...ariaProps}
            onKeyDownCapture={(e) => {
                // if we're in an input (such as renaming an environment), we want to prevent the grid from capturing the key events
                if (isEditableElement(e.target)) {
                    return;
                }

                onKeyDownCapture?.(e);
            }}
            ref={ref}
            className="mx-[1px] my-1 flex flex-col gap-1"
        >
            {[...state.collection].map((item) => (
                <EnvListItem
                    key={item.key}
                    item={item}
                    state={state}
                    agentExecutions={agentExecutionsByEnvironment.get(item.value?.id ?? "") ?? []}
                    childEnvironments={childEnvironments.get(item.value?.id ?? "") ?? []}
                    environmentsById={environmentsById}
                />
            ))}
        </ul>
    );
};

const EnvListItem: FC<{
    item: Node<PlainEnvironment>;
    state: ListState<PlainEnvironment>;
    agentExecutions: PlainAgentExecution[];
    childEnvironments: ChildEnvironmentInfo[];
    environmentsById: Map<string, PlainEnvironment>;
}> = ({ item, state, agentExecutions, childEnvironments, environmentsById }) => {
    const ref = useRef<HTMLButtonElement>(null);
    const { rowProps, gridCellProps } = useGridListItem({ node: item }, state, ref);
    const { onKeyDownCapture: rowKeyDownCapture, ...restRowProps } = rowProps;
    const location = useLocation();
    const agentExecution = useMemo(() => mostRecentAgentExecution(agentExecutions), [agentExecutions]);

    if (!item.value) {
        return null;
    }

    return (
        <li
            {...restRowProps}
            onKeyDownCapture={(e) => {
                // Skip react-aria's grid navigation when focus is inside a
                // text input (e.g. the rename editor) so arrow keys move the
                // caret instead of shifting focus between cells.
                const target = e.target as HTMLElement;
                if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") {
                    return;
                }
                rowKeyDownCapture?.(e);
            }}
        >
            <div {...gridCellProps}>
                <Env
                    ref={ref}
                    env={item.value}
                    active={location.pathname.includes("/" + item.value.id)}
                    agentExecution={agentExecution}
                />
                {childEnvironments.length > 0 && (
                    <ChildEnvironmentsList
                        childEnvironments={childEnvironments}
                        environmentsById={environmentsById}
                        currentPath={location.pathname}
                    />
                )}
            </div>
        </li>
    );
};

/** Curved connector line from the vertical trunk to a child entry. */
const ChildConnector: FC = () => (
    <svg
        width="14"
        height="30"
        viewBox="0 0 14 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="shrink-0"
        aria-hidden
    >
        <g transform="translate(0 -11)">
            <path
                d="M0.499954 0.499999L0.499956 28.5C0.499957 35.1274 5.87254 40.5 12.5 40.5L13.5 40.5"
                stroke="currentColor"
                strokeLinecap="round"
            />
        </g>
    </svg>
);

/**
 * Renders the list of child (sub-agent) environments with connector lines.
 * Each child gets a curved connector; a vertical trunk line connects them.
 */
const ChildEnvironmentsList: FC<{
    childEnvironments: ChildEnvironmentInfo[];
    environmentsById: Map<string, PlainEnvironment>;
    currentPath: string;
}> = ({ childEnvironments, environmentsById, currentPath }) => {
    // Resolve children to their PlainEnvironment objects, filtering out missing ones
    const resolvedChildren = useMemo(
        () =>
            childEnvironments
                .map((child) => {
                    const env = environmentsById.get(child.environmentId);
                    return env ? { env, agentExecution: child.agentExecution } : null;
                })
                .filter((c): c is { env: PlainEnvironment; agentExecution: PlainAgentExecution } => c !== null),
        [childEnvironments, environmentsById],
    );

    if (resolvedChildren.length === 0) {
        return null;
    }

    // Layout geometry (px):
    //   Child row: py-0.5 (2px) + h-9 (36px) + py-0.5 (2px) = 40px total
    //   Status dot: size-6 (24px), vertically centered in h-9
    //   Dot center from row top = 2 + (36-24)/2 + 12 = 20
    //
    // Connector SVG: 14×41, curve endpoint at (13.5, 40.5).
    // Place SVG so its bottom aligns with dot center:
    //   svgTop = dotCenter - svgHeight = 20 - 41 = -21
    const DOT_CENTER = 15;
    const SVG_H = 25;

    return (
        <div className="ml-[12px] flex flex-col">
            {resolvedChildren.map((child, index) => (
                <div key={child.env.id} className="relative flex">
                    {/* Connector gutter — 14px matches SVG width */}
                    <div className="relative w-[14px] shrink-0 text-border-strong" aria-hidden>
                        {/* Vertical trunk between siblings.
                            Runs from dot-center to the row bottom. The next row's
                            SVG vertical segment overlaps upward to close the gap.
                            Hidden on the last child. */}
                        {index < resolvedChildren.length - 1 && (
                            <div
                                className="absolute bottom-0 w-px bg-current"
                                style={{ left: 4, top: DOT_CENTER - 10 }}
                            />
                        )}
                        {/* Curved connector — bottom of SVG aligns with dot center */}
                        <div className="absolute left-1" style={{ top: DOT_CENTER - SVG_H }}>
                            <ChildConnector />
                        </div>
                    </div>
                    <div className="min-w-0 flex-1">
                        <NestedChildEnvironment
                            env={child.env}
                            agentExecution={child.agentExecution}
                            active={currentPath.includes("/" + child.env.id)}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

/**
 * Renders a child (sub-agent) environment nested under its parent.
 * Shows status dot, name, and conversation metadata.
 */
const NestedChildEnvironment: FC<{
    env: PlainEnvironment;
    agentExecution: PlainAgentExecution;
    active: boolean;
}> = ({ env, agentExecution, active }) => {
    const envName = useEnvironmentName(env, agentExecution);
    const branchName = useBranch(env) ?? "main";
    const { selectedEditor } = useGetEditorForEnv(env.id);
    const navigate = useNavigate();
    const handleGoToProject = useMemo(() => {
        const projectId = env.metadata?.projectId;
        if (!projectId) return undefined;
        return () => navigate(`/projects/${projectId}/settings`);
    }, [env.metadata?.projectId, navigate]);

    const className = cn(
        "group w-full select-none rounded-lg py-0.5 pr-2",
        "flex items-center",
        active ? "bg-surface-hover" : "hover:bg-surface-hover",
    );

    return (
        <EnvironmentActionsMenu
            environment={env}
            defaultEditor={selectedEditor}
            startRename={undefined}
            handleGoToProject={handleGoToProject}
            renderAs="contextMenu"
            agentExecution={agentExecution}
        >
            <Link
                className={className}
                to={getDetailsURL({ environment: env })}
                aria-label={envName}
                data-testid={`child-environment-entry-${env.id}`}
            >
                <div className="flex h-9 min-w-0 items-center gap-1">
                    <EnvironmentStatusIndicator env={env} agentExecution={agentExecution} />
                    <div className="flex min-w-0 flex-1 flex-col truncate">
                        <EnvironmentName
                            name={envName}
                            branchName={branchName}
                            agentExecution={agentExecution}
                            className="h-5"
                        />
                        <ConversationMetadata env={env} agentExecution={agentExecution} branchName={branchName} />
                    </div>
                </div>
            </Link>
        </EnvironmentActionsMenu>
    );
};

const Env = React.forwardRef<
    HTMLButtonElement,
    {
        env: PlainEnvironment;
        active?: boolean;
        agentExecution?: PlainAgentExecution;
    }
>(({ env, active, agentExecution }, ref) => {
    const { selectedEditor } = useGetEditorForEnv(env.id);

    const [renamingId, setRenamingId] = useAtom(renamingEnvironmentIdAtom);
    const isRenaming = renamingId === env.id;
    const stopRenaming = useCallback(() => {
        setRenamingId((prev) => (prev === env.id ? null : prev));
    }, [env.id, setRenamingId]);
    const startRenaming = useCallback(() => setRenamingId(env.id), [env.id, setRenamingId]);
    const [renameRequested, setRenameRequested] = useAtom(renameEnvironmentAtom(env.id));
    useEffect(() => {
        if (renameRequested) {
            setRenamingId(env.id);
            setRenameRequested(false);
        }
    }, [renameRequested, setRenameRequested, env.id, setRenamingId]);
    const [isContextMenuOpen, setIsContextMenuOpen] = useAtom(isEnvContextMenuOpenAtom(env.id));
    const [, setIsEnvDropdownOpen] = useAtom(isEnvDropdownOpenAtomFamily(env.id));
    const envName = useEnvironmentName(env, agentExecution);
    const [newName, setNewName] = useState(envName);
    const updateEnvironment = useUpdateEnvironment();
    const branchName = useBranch(env) ?? "main";
    const { toast } = useToast();
    const isPointerCoarse = useIsPointerCoarse();

    const { longPressProps } = useLongPress({
        isDisabled: !isPointerCoarse,
        onLongPress: () => {
            haptic("confirm");
            setIsEnvDropdownOpen(true);
        },
        accessibilityDescription: "Long press to open environment actions menu",
    });

    useEffect(() => {
        if (envName !== newName && !isRenaming) {
            // Ensure that the newName always reflects the current environment name.
            // The name can change if e.g. an agent execution is added to the environment, or its name changed.
            setNewName(envName);
        }
    }, [envName, isRenaming, newName]);

    const handleSave = useCallback(async () => {
        if (newName === envName) {
            stopRenaming();
            return;
        }

        if (updateEnvironment.isPending) {
            return;
        }

        try {
            await updateEnvironment.mutateAsync({
                req: new UpdateEnvironmentRequest({
                    environmentId: env.id,
                    metadata: new UpdateEnvironmentRequest_Metadata({
                        name: newName,
                    }),
                }),
            });
            if (newName === "") {
                toast({ title: `Environment name removed` });
            } else {
                toast({ title: `Environment renamed to "${newName}"` });
            }
        } catch (err) {
            toast({
                title: "Failed to rename environment",
                description: formatError(err),
            });
        }
        stopRenaming();
    }, [env.id, newName, envName, updateEnvironment, toast, stopRenaming]);

    const navigate = useNavigate();
    const handleGoToProject = useMemo(() => {
        const projectId = env.metadata?.projectId;
        if (!projectId) return undefined;
        return () => navigate(`/projects/${projectId}/settings`);
    }, [env.metadata?.projectId, navigate]);

    return (
        <EnvironmentActionsMenu
            environment={env}
            defaultEditor={selectedEditor}
            startRename={startRenaming}
            handleGoToProject={handleGoToProject}
            renderAs="contextMenu"
            onOpenChange={setIsContextMenuOpen}
            agentExecution={agentExecution}
        >
            {/* Use relative + absolute overlay so the EnvironmentEntry always
                stays in the layout. This keeps the dropdown trigger's bounding
                rect valid during the Radix close animation, preventing the
                portaled dropdown content from jumping to (0,0). */}
            <div className="relative">
                {isRenaming && (
                    <div
                        className={cn(
                            "absolute inset-0 z-10",
                            "group w-full rounded-lg",
                            "p-1",
                            "flex items-center justify-between",
                            active ? "bg-surface-secondary" : "bg-surface-primary hover:bg-surface-03",
                            isContextMenuOpen && !active && "bg-surface-03",
                        )}
                    >
                        <div className={cn("flex min-w-0 flex-1 items-center gap-1")}>
                            <EnvironmentStatusIndicator env={env} agentExecution={agentExecution} />
                            <EnvironmentNameEditor
                                newName={newName}
                                onChange={setNewName}
                                onCancel={() => {
                                    stopRenaming();
                                    setNewName(envName);
                                }}
                                onSave={handleSave}
                            />
                        </div>
                    </div>
                )}
                {/* Keep EnvironmentEntry always mounted so react-aria's grid
                    list ref is preserved and the dropdown trigger retains a
                    valid bounding rect for Radix's Popper positioning. */}
                <div
                    style={isRenaming ? { visibility: "hidden", pointerEvents: "none" } : undefined}
                    aria-hidden={isRenaming || undefined}
                >
                    <EnvironmentEntry
                        env={env}
                        envName={envName}
                        branchName={branchName}
                        selectedEditor={selectedEditor}
                        active={active}
                        onRename={startRenaming}
                        ref={ref}
                        agentExecution={agentExecution}
                        longPressProps={isPointerCoarse ? longPressProps : undefined}
                    />
                </div>
            </div>
        </EnvironmentActionsMenu>
    );
});
Env.displayName = "Env";

const EnvironmentEntry = React.forwardRef<
    HTMLElement,
    {
        env: PlainEnvironment;
        envName: string;
        branchName: string;
        selectedEditor: PlainEditor | undefined;
        active?: boolean;
        onRename: () => void;
        agentExecution?: PlainAgentExecution;
        longPressProps?: React.HTMLAttributes<HTMLElement>;
    }
>(({ env, envName, branchName, selectedEditor, active, onRename, agentExecution, longPressProps }, ref) => {
    const [isDropdownMenuOpen] = useAtom(isEnvDropdownOpenAtomFamily(env.id));
    const [isContextMenuOpen] = useAtom(isEnvContextMenuOpenAtom(env.id));
    // Keep the row highlighted while the menu exit-animates.
    const { shouldRender: isActedUpon } = useExitTransition(isDropdownMenuOpen || isContextMenuOpen, { duration: 150 });

    const { isMobileViewport: isMobile } = useIsMobileViewport();

    // Check if there are uncommitted changes
    const hasChanges = (gitStatusChangesCount(env.status?.content?.git) ?? 0) > 0;

    const commonClassName = cn(
        "group w-full select-none rounded-lg p-1 pr-2",
        "flex items-center justify-between",
        // Add gap-1 when there are changes or on hover (when commands are visible)
        hasChanges && "gap-1",
        "group-hover:gap-1 group-focus-within:gap-1",
        active ? "bg-surface-hover" : "hover:bg-surface-hover",
        isActedUpon && !active && "bg-surface-hover",
    );

    if (active) {
        return (
            <div
                className={commonClassName}
                ref={ref as React.RefObject<HTMLDivElement>}
                tabIndex={-1}
                data-testid={`environment-entry-${env.id}`}
                {...longPressProps}
            >
                <div className="flex h-9 min-w-0 flex-1 items-center gap-1">
                    <EnvironmentStatusIndicator env={env} agentExecution={agentExecution} />
                    {isMobile ? (
                        <div className="flex min-w-0 flex-1 flex-col truncate text-left">
                            <EnvironmentName
                                name={envName}
                                branchName={branchName}
                                agentExecution={agentExecution}
                                className="h-5"
                            />
                            <ConversationMetadata env={env} agentExecution={agentExecution} branchName={branchName} />
                        </div>
                    ) : (
                        <button
                            className="flex min-w-0 flex-1 flex-col truncate text-left"
                            aria-label={`Rename ${envName}`}
                            onClick={(e) => {
                                e.preventDefault();
                                onRename();
                            }}
                            tabIndex={-1}
                            data-tracking-id="rename-environment-sidebar-environment-list"
                        >
                            <EnvironmentName
                                name={envName}
                                branchName={branchName}
                                agentExecution={agentExecution}
                                className="h-5"
                            />
                            <ConversationMetadata env={env} agentExecution={agentExecution} branchName={branchName} />
                        </button>
                    )}
                </div>

                <div className="mr-0.5 flex items-center gap-0.5">
                    <Suspense>
                        <EnvironmentLockIndicator env={env} />
                    </Suspense>
                    <EnvironmentSidebarActions
                        env={env}
                        defaultEditor={selectedEditor}
                        startRename={onRename}
                        agentExecution={agentExecution}
                    />
                    <EnvironmentChanges git={env.status?.content?.git} />
                </div>
            </div>
        );
    }

    return (
        <Link
            ref={ref as React.RefObject<HTMLAnchorElement>}
            className={commonClassName}
            to={getDetailsURL({ environment: env })}
            aria-label={envName}
            data-testid={`environment-entry-${env.id}`}
            {...longPressProps}
        >
            <div className="flex h-9 min-w-0 items-center gap-1">
                <EnvironmentStatusIndicator env={env} agentExecution={agentExecution} />
                <div className="flex min-w-0 flex-1 flex-col truncate">
                    <EnvironmentName
                        name={envName}
                        branchName={branchName}
                        agentExecution={agentExecution}
                        className="h-5"
                    />
                    <ConversationMetadata env={env} agentExecution={agentExecution} branchName={branchName} />
                </div>
            </div>

            <div
                className="mr-0.5 flex items-center gap-0.5"
                onClick={(e) => {
                    e.stopPropagation(); // prevent the link from being clicked as well
                }}
                data-tracking-id="open-environment-actions-sidebar-environment-list"
            >
                <Suspense>
                    <EnvironmentLockIndicator env={env} />
                </Suspense>
                <EnvironmentSidebarActions
                    env={env}
                    defaultEditor={selectedEditor}
                    startRename={onRename}
                    agentExecution={agentExecution}
                />
                <EnvironmentChanges git={env.status?.content?.git} />
            </div>
        </Link>
    );
});
EnvironmentEntry.displayName = "EnvironmentEntry";

export const EnvironmentName: FC<
    {
        name: string;
        branchName: string;
        size?: "sm" | "base" | "lg";
        agentExecution?: PlainAgentExecution;
    } & PropsWithClassName
> = ({ name, branchName, size = "base", agentExecution, className }) => {
    const Comp = agentExecutionLoading(agentExecution) ? TextShimmer : "span";

    return (
        <SkeletonText size={size} className="w-32" ready={!!name}>
            {name === branchName && (
                <span
                    className={cn("inline-block truncate", {
                        "text-sm": size === "sm",
                        "text-base": size === "base",
                        "text-lg": size === "lg",
                    })}
                    translate="no"
                >
                    {name}
                </span>
            )}
            {name !== branchName && (
                <Tooltip content={"Branch: " + branchName} usePortal={true}>
                    <Comp
                        className={cn(
                            "inline-block truncate",
                            {
                                "text-sm": size === "sm",
                                "text-base": size === "base",
                                "text-lg": size === "lg",
                                "font-bold": agentExecutionNeedsAttention(agentExecution),
                            },
                            className,
                        )}
                        translate="no"
                    >
                        {name}
                    </Comp>
                </Tooltip>
            )}
        </SkeletonText>
    );
};

export const EnvironmentNameEditor: FC<{
    newName: string;
    onChange: (value: string) => void;
    onSave: () => void;
    onCancel: () => void;
}> = ({ newName, onChange, onSave, onCancel }) => {
    const inputId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    // Set by the Save button's pointerdown or form submit so the blur handler
    // knows not to cancel while a save is in progress.
    const saveIntended = useRef(false);
    // Tracks whether a pointerdown happened outside the form. Set by a
    // document-level listener so the blur handler can distinguish a real
    // click-outside from Radix's programmatic focus restoration (which
    // produces a blur with relatedTarget=null and no preceding pointer event).
    const pointerDownOutside = useRef(false);
    const formRef = useRef<HTMLFormElement>(null);
    // Ensure focus lands on the input even when react-aria's grid list tries to reclaim it
    useEffect(() => {
        requestAnimationFrame(() => inputRef.current?.focus());
    }, []);
    useEffect(() => {
        const handler = (e: PointerEvent) => {
            if (formRef.current && !formRef.current.contains(e.target as HTMLElement)) {
                pointerDownOutside.current = true;
            }
        };
        document.addEventListener("pointerdown", handler, true);
        return () => document.removeEventListener("pointerdown", handler, true);
    }, []);

    return (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.preventDefault();
                saveIntended.current = true;
                onSave();
            }}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") {
                    e.preventDefault();
                    onCancel();
                } else if (e.key === "Enter") {
                    // Mark save as intended so a blur that fires between
                    // keydown and the form's submit event doesn't cancel.
                    saveIntended.current = true;
                }
            }}
            onBlur={(e: React.FocusEvent<HTMLFormElement>) => {
                const relatedTarget = e.relatedTarget as HTMLElement | null;
                const form = e.currentTarget;
                // Ignore internal focus moves (e.g. input → Save/Cancel button)
                if (relatedTarget && form.contains(relatedTarget)) {
                    return;
                }
                if (!document.hasFocus()) {
                    return;
                }
                // Radix's FocusScope restores focus via setTimeout(0) when the
                // context menu closes, producing a blur with no preceding
                // pointer event. Reclaim focus in that case. A real click-
                // outside always fires pointerdown first, which sets the flag.
                if (!pointerDownOutside.current && !saveIntended.current) {
                    requestAnimationFrame(() => inputRef.current?.focus());
                    return;
                }
                pointerDownOutside.current = false;
                // The Save button sets saveIntended via pointerdown (which
                // fires before blur). When set, we let the button's onClick
                // handle the save and do nothing here to avoid a double call.
                // Otherwise clicking outside dismisses without saving.
                if (!saveIntended.current) {
                    onCancel();
                }
            }}
            className="flex flex-1 items-center gap-0.5"
        >
            <Label className="sr-only" htmlFor={inputId}>
                Custom environment name. Leave blank to use the branch name.
            </Label>
            <Input
                ref={inputRef}
                id={inputId}
                className="h-9 border-0 px-0 focus-visible:ring-0"
                value={newName}
                maxLength={128}
                autoComplete="off"
                type="text"
                onChange={(value) => {
                    onChange(value.target.value);
                }}
                autoFocus={true}
            />

            <EnvironmentSidebarRenameActions
                onSave={onSave}
                onCancel={onCancel}
                onSavePointerDown={() => (saveIntended.current = true)}
            />
        </form>
    );
};

export const EnvironmentChanges: FC<{ git?: PlainMessage<EnvironmentGitStatus> } & PropsWithClassName> = ({
    git,
    className,
}) => {
    const changes = gitStatusChangesCount(git) ?? 0;

    if (changes === 0) {
        return null;
    }

    return (
        <Tooltip
            content={<GitStatusTooltipContentFromGit git={git} />}
            inverted={false}
            usePortal={true}
            side="bottom"
            align="start"
            collisionPadding={{ top: 8, bottom: 8, left: 8, right: -450 }}
            className="m-0 mt-1 min-w-[250px] max-w-[450px] p-0 shadow-md"
        >
            <Notification content={formatChangesCount(changes)} className={className} />
        </Tooltip>
    );
};

const isEnvDropdownOpenAtomFamily = atomFamily((_environmentId: string) => atom(false));
const isEnvContextMenuOpenAtom = atomFamily((_environmentId: string) => atom(false));

const EnvironmentSidebarActions: FC<{
    env: PlainEnvironment;
    defaultEditor: PlainEditor | undefined;
    startRename?: () => void;
    agentExecution?: PlainAgentExecution;
}> = ({ env, defaultEditor, startRename, agentExecution }) => {
    const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useAtom(isEnvDropdownOpenAtomFamily(env.id));
    const [isContextMenuOpen] = useAtom(isEnvContextMenuOpenAtom(env.id));
    // Keep the trigger button visible while the dropdown exit-animates,
    // preventing the menu from losing its anchor and shifting.
    const { shouldRender: isActedUpon } = useExitTransition(isDropdownMenuOpen || isContextMenuOpen, { duration: 150 });
    const { value: isSuperOnaEnabled } = useFlagSuperOnaEnabled();
    const navigate = useNavigate();
    const handleViewDetails = useCallback(() => {
        navigate(`${getDetailsURL({ environment: env })}?view=environment`);
    }, [navigate, env]);
    const handleGoToProject = useMemo(() => {
        const projectId = env.metadata?.projectId;
        if (!projectId) return undefined;
        return () => navigate(`/projects/${projectId}/settings`);
    }, [env.metadata?.projectId, navigate]);
    return (
        <VisibleOnActive className={cn("flex gap-0.5", { "not-sr-only": isActedUpon })}>
            <EnvironmentActionsMenu
                renderAs="dropdownMenu"
                environment={env}
                defaultEditor={defaultEditor}
                startRename={startRename}
                onOpenChange={setIsDropdownMenuOpen}
                agentExecution={agentExecution}
                open={isDropdownMenuOpen}
                handleViewDetails={isSuperOnaEnabled ? handleViewDetails : undefined}
                handleGoToProject={handleGoToProject}
            >
                <span className="flex items-center">
                    {/* we wrap with a span because if EnvironmentActionsMenu is the direct parent of Tooltip, we would be nesting asChild-s */}
                    <Tooltip content="Open, logs, and more" usePortal>
                        <Button
                            LeadingIcon={IconEllipsisHorizontal}
                            size="xs"
                            variant="ghost"
                            type="button"
                            aria-label="Open, logs, and more"
                            className="border-0 text-content-secondary"
                            // Setting the data state so they work the same as if this button was the direct trigger.
                            data-state={isDropdownMenuOpen ? "open" : "closed"}
                            data-open={isDropdownMenuOpen ? "open" : "closed"}
                            tabIndex={-1}
                        />
                    </Tooltip>
                </span>
            </EnvironmentActionsMenu>
        </VisibleOnActive>
    );
};

const EnvironmentSidebarRenameActions: FC<{
    onSave: () => void;
    onCancel: () => void;
    onSavePointerDown?: () => void;
}> = ({ onSave, onCancel, onSavePointerDown }) => {
    return (
        <div className="mr-0.5 flex items-center gap-0.5">
            <Tooltip content={`Save`} usePortal={true}>
                <Button
                    onPointerDown={(e) => {
                        // Signal the blur handler that this is an intentional
                        // save so it doesn't cancel. Must be pointerdown (fires
                        // before blur) rather than click (fires after blur).
                        e.stopPropagation();
                        onSavePointerDown?.();
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSave();
                    }}
                    LeadingIcon={withGitpodSize(IconCheckmark2Small)}
                    size="xs"
                    variant="ghost"
                    aria-label={`Save`}
                    // deliberately not setting tabIndex to -1 here like on all other cell actions, because in renaming mode, we want these controls to remain accessible (arrow keys are not appropriate there, since the user is in a text input)
                    className="border-0 text-content-secondary"
                    data-tracking-id="confirm-save-environment-rename-sidebar-environment-list"
                />
            </Tooltip>
            <Tooltip content={`Cancel`} usePortal={true}>
                <Button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCancel();
                    }}
                    LeadingIcon={withGitpodSize(IconCrossSmall)}
                    size="xs"
                    variant="ghost"
                    aria-label={`Cancel`}
                    className="border-0 text-content-secondary"
                    data-tracking-id="cancel-environment-rename-sidebar-environment-list"
                />
            </Tooltip>
        </div>
    );
};

export const EnvironmentStatusIndicator: FC<{ env: PlainEnvironment; agentExecution?: PlainAgentExecution }> = ({
    env,
    agentExecution,
}) => {
    const [isDropdownMenuOpen] = useAtom(isEnvDropdownOpenAtomFamily(env.id));
    const [isContextMenuOpen] = useAtom(isEnvContextMenuOpenAtom(env.id));
    // Keep the action overlay visible while the menu exit-animates.
    const { shouldRender: isActedUpon } = useExitTransition(isDropdownMenuOpen || isContextMenuOpen, { duration: 150 });

    const { handleStartError, modalProps } = useLifetimeEnforcement();

    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();
    const { toast } = useToast();

    const handleStart = useCallback(
        async (e: React.SyntheticEvent) => {
            e.stopPropagation();
            e.preventDefault();
            try {
                await startEnvironment.mutateAsync({
                    environmentId: env.id,
                    skipPolicyCheck: true,
                });
            } catch (error) {
                if (handleStartError(error)) {
                    return;
                }
                toast({
                    title: "Failed to start environment",
                    description: formatError(error),
                });
            }
        },
        [startEnvironment, env.id, handleStartError, toast],
    );

    const handleStop = useCallback(
        async (e: React.SyntheticEvent) => {
            e.stopPropagation();
            e.preventDefault();
            try {
                await stopEnvironment.mutateAsync(env.id);
            } catch (error) {
                toast({
                    title: "Failed to stop environment",
                    description: formatError(error),
                });
            }
        },
        [stopEnvironment, toast, env.id],
    );

    const hasReachedStablePhase = environmentHasReachedStablePhase(env);
    const canStartEnvironment = useCanStartEnvironment(env);
    const { value: isSuperOnaEnabled } = useFlagSuperOnaEnabled();
    const envState = stateFromEnvironment(env);
    const showActionOverlay = hasReachedStablePhase || (canStartEnvironment && envState === "stopping");
    const dotProps = isSuperOnaEnabled ? getConversationDotProps(env, agentExecution, { superOna: true }) : undefined;

    return (
        <>
            <div className="relative size-6">
                {dotProps ? (
                    <StatusDot
                        size="lg"
                        color={dotProps.color}
                        variant={dotProps.variant}
                        animation={dotProps.animation}
                        tooltip={dotProps.tooltip}
                        className={cn({
                            invisible: showActionOverlay && isActedUpon,
                            "group-focus-within:invisible group-hover:invisible": showActionOverlay,
                        })}
                    />
                ) : (
                    <EnvironmentStatusDot
                        env={env}
                        size="lg"
                        className={cn({
                            invisible: showActionOverlay && isActedUpon,
                            "group-focus-within:invisible group-hover:invisible": showActionOverlay,
                        })}
                    />
                )}
                <div
                    className={cn("absolute inset-0 hidden size-6 group-data-[state=open]/actions-context-menu:block", {
                        block: showActionOverlay && isActedUpon,
                        "group-focus-within:block group-hover:block": showActionOverlay,
                    })}
                >
                    {showActionOverlay && (
                        <>
                            {showStart(env) && (
                                <Tooltip content="Start Environment" usePortal={true}>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        LeadingIcon={IconPlay}
                                        onClick={handleStart}
                                        loading={startEnvironment.isPending}
                                        aria-label="Start Environment"
                                        className="size-6 border-0 text-content-secondary"
                                        tabIndex={-1} // exclude from tab order, navigation done by EnvListItem
                                        data-tracking-id="start-environment-environment-status-indicator"
                                    />
                                </Tooltip>
                            )}
                            {showStop(env) && (
                                <Tooltip content="Stop Environment" usePortal={true}>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        LeadingIcon={IconStop}
                                        onClick={handleStop}
                                        loading={stopEnvironment.isPending}
                                        aria-label="Stop Environment"
                                        className="size-6 border-0 text-content-secondary"
                                        tabIndex={-1}
                                        data-tracking-id="stop-environment-environment-status-indicator"
                                    />
                                </Tooltip>
                            )}
                        </>
                    )}
                </div>
            </div>
            <EnvironmentLifetimeExceededModal environment={env} {...modalProps} />
        </>
    );
};

/**
 * Skeleton entry that mimics a conversation/environment row:
 * circle (20x20) + large rect (16px, 100% width) + small rect (16px, 50% width)
 */
const SkeletonEntry: FC<{ delay?: number; opacity?: number }> = ({ delay = 0, opacity = 1 }) => {
    const style: React.CSSProperties = {
        ...(delay > 0 && { animationDelay: `${delay}ms` }),
        opacity,
    };
    return (
        <div className="flex items-center gap-2 px-2 py-1" style={{ opacity }}>
            {/* Circle indicator */}
            <SkeletonBlock ready={false} className="size-5 shrink-0 rounded-full" style={style} />
            {/* Text content */}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <SkeletonBlock ready={false} className="h-4 w-full rounded" style={style} />
                <SkeletonBlock ready={false} className="h-4 w-1/2 rounded" style={style} />
            </div>
        </div>
    );
};

/**
 * Skeleton loader for the sidebar environment list.
 * Displays placeholder content while environments are loading.
 */
const SidebarEnvironmentListSkeleton: FC = () => {
    return (
        <div className="flex flex-col" data-testid="environments-list-skeleton">
            {/* Group header */}
            <div className="flex h-9 w-full flex-row items-center pl-3">
                <span className="text-sm font-medium text-content-inactive">Projects</span>
            </div>
            {/* Skeleton entries with staggered animation and decreasing opacity */}
            <SkeletonEntry delay={0} opacity={1} />
            <SkeletonEntry delay={150} opacity={0.8} />
            <SkeletonEntry delay={300} opacity={0.6} />
        </div>
    );
};
